import React, {ComponentProps} from 'react'
import {SxProp} from '@fuegokit/react/sx'
import {BaseStackProps} from './types'
import {RecursiveStack, NonRecursiveStack} from './styles'

/**
 *
 * The Stack component is a low-level primitive component for arranging items in a row or vertical stack, with even spacing between the items.
 *
 * Use the Stack component for small-scale layout tasks and avoiding redundant margin or padding properties that can double the intended space.
 *
 * Use the `size` prop to control the spacing between elements in the Stack component.
 *
 * The spacing between elements in the Stack is controlled using values from the spacing scale, with a default value set at 3 (8px).
 *
 * By default, the Stack component arranges items vertically in a column, but the direction prop can be used to position items rowly in a row as well.
 *
 */
const Stack = ({
  children,
  as = 'div',
  sx: sxProp,
  direction = 'column',
  align,
  justify,
  recursive = false,
  size = 3
}: React.PropsWithChildren<BaseStackProps & SxProp>) => {
  let Component
  if (recursive) {
    Component = RecursiveStack
  } else {
    Component = NonRecursiveStack
  }
  return (
    <Component as={as} sx={sxProp} size={size} direction={direction} align={align} justify={justify}>
      {children}
    </Component>
  )
}

export default Stack
export type StackProps = ComponentProps<typeof Stack>
